import { PointerEvent, useContext, useEffect, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { HistoryIcon } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from 'components/Popover';
import { BaseOptionType, Select } from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadSearchContext } from 'contexts/loadSearchContext';
import { SidebarViewContext } from 'contexts/sidebarViewContext';
import useTMSContext from 'hooks/useTMSContext';
import { NormalizedLoad } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import SidebarView from 'types/enums/SidebarView';
import { CustomerRateSource } from 'types/suggestions/LoadBuildingSuggestions';

import { RevenovaTenant, currencyList, pricingOptions } from './constants';
import { getTenantFromString } from './helpers';

interface RatesFormProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  // The booked load the suggested line haul came from when the email stated none.
  lineHaulSource?: CustomerRateSource;
}

// formatPickupDay turns a YYYY-MM-DD day into "Oct 12" without shifting it across time zones.
function formatPickupDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) {
    return '';
  }
  return new Date(year, month - 1, date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const HOVER_CLOSE_DELAY_MS = 180;

// LineHaulSourceTag is a history icon beside the line haul label. Hovering or clicking it shows the booked load
// the suggested rate came from, with a link that opens it in Load View like the clone card does.
function LineHaulSourceTag({ source }: { source: CustomerRateSource }) {
  const { addTab } = useContext(LoadSearchContext);
  const { setCurrentView } = useContext(SidebarViewContext);
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<Maybe<ReturnType<typeof setTimeout>>>(null);
  const pickupDay = source.pickupDate ? formatPickupDay(source.pickupDate) : '';

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(
      () => setOpen(false),
      HOVER_CLOSE_DELAY_MS
    );
  };
  useEffect(() => cancelClose, []);

  const openOnHover = (e: PointerEvent) => {
    cancelClose();
    if (e.pointerType === 'mouse') {
      setOpen(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Rate from the last load on this lane'
          data-testid='revenova-line-haul-source'
          className='inline-flex shrink-0 cursor-pointer items-center rounded-sm p-0.5 text-brand-main hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main'
          onPointerEnter={openOnHover}
          onPointerLeave={scheduleClose}
          // The tag sits inside the input's label; keep a click from focusing the input.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <HistoryIcon
            className='h-3.5 w-3.5 !stroke-brand-main'
            aria-hidden='true'
          />
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align='start'
          className='w-56 p-3'
          aria-label='Line haul source'
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
        >
          <Typography variant='body-xs' weight='semibold'>
            Rate from the last load on this lane
          </Typography>
          <Typography variant='body-xs' textColor='lessMuted' className='mt-1'>
            <Button
              buttonNamePosthog={null}
              variant='unstyled'
              size='auto'
              type='button'
              aria-label={`Open load ${source.freightTrackingID}`}
              className='inline rounded-sm p-0 text-xs font-semibold text-brand-main underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main'
              onClick={() => {
                setOpen(false);
                addTab(source.freightTrackingID, 'load');
                setCurrentView(SidebarView.Loads);
              }}
            >
              Load {source.freightTrackingID}
            </Button>
            {pickupDay && `, picked up ${pickupDay}`}
          </Typography>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

export function RatesForm({ formMethods, lineHaulSource }: RatesFormProps) {
  const { control, watch, resetField, getFieldState, formState } = formMethods;
  const { tenant } = useTMSContext();

  const isCoolerTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.Cooler
    : false;
  const isGreatLakesTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.GreatLakes
    : false;

  const watchedTotalAmount = watch('rateData.customerTotalCharge.val');
  const watchedCustomerLineHaulCharge = watch(
    'rateData.customerLineHaulCharge.val'
  );
  const watchedCustomerFuelSurcharge = watch('rateData.fscFlatRate');
  // The tag describes the suggested value, so it goes once the rep changes it.
  const showLineHaulSource =
    !!lineHaulSource &&
    !getFieldState('rateData.customerLineHaulCharge.val', formState).isDirty;

  // Auto-calculate total: Line Haul Charge + Fuel Surcharge
  useEffect(() => {
    const lineHaul = Number(watchedCustomerLineHaulCharge) || 0;
    const fuelSurcharge = Number(watchedCustomerFuelSurcharge) || 0;
    const calculatedTotal = lineHaul + fuelSurcharge;

    if (calculatedTotal !== watchedTotalAmount) {
      // Derived field — update baseline so Load View doesn't mark it dirty on open.
      resetField('rateData.customerTotalCharge.val', {
        defaultValue: calculatedTotal,
      });
    }
  }, [
    watchedCustomerLineHaulCharge,
    watchedCustomerFuelSurcharge,
    watchedTotalAmount,
    resetField,
  ]);

  return (
    <Grid cols='2' gap='sm' width='full' className='mx-0'>
      <Grid
        cols='2'
        gap='sm'
        width='full'
        align='stretch'
        className='col-span-2 [&>label]:h-full [&>label]:justify-end'
      >
        <RHFTextInput
          name='rateData.customerLineHaulCharge.val'
          label={
            isGreatLakesTenant ? 'Net Freight Charges' : 'Line Haul Charge'
          }
          hideAIHint={showLineHaulSource}
          labelAdornment={
            showLineHaulSource &&
            lineHaulSource && <LineHaulSourceTag source={lineHaulSource} />
          }
          options={{ valueAsNumber: true }}
        />

        <RHFTextInput
          name='rateData.fscFlatRate'
          label='Fuel Surcharge'
          options={{ valueAsNumber: true }}
        />
      </Grid>

      <Grid
        cols='2'
        gap='sm'
        width='full'
        align='stretch'
        className='col-span-2 [&>label]:h-full [&>label]:justify-end'
      >
        <RHFTextInput
          name='rateData.customerTotalCharge.val'
          label='Total Amount'
          options={{ valueAsNumber: true }}
        />

        <FormInputWrapper
          name='rateData.customerTotalCharge.unit'
          label='Currency'
          required={
            watchedTotalAmount > 0 ||
            watchedCustomerLineHaulCharge > 0 ||
            (watchedCustomerFuelSurcharge ?? 0) > 0
          }
        >
          <Controller
            name='rateData.customerTotalCharge.unit'
            control={control}
            rules={{
              required:
                watchedTotalAmount > 0 ||
                watchedCustomerLineHaulCharge > 0 ||
                (watchedCustomerFuelSurcharge ?? 0) > 0,
            }}
            render={({ field }) => (
              <Select
                showSearch={currencyList && currencyList.length > 1}
                name={field.name}
                placeholder='Choose'
                optionFilterProp='children'
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  (
                    option?.label?.toString().toLocaleLowerCase() ?? ''
                  ).includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  (optionA?.label?.toString() ?? '')
                    .toLowerCase()
                    .localeCompare(
                      (optionB?.label?.toString() ?? '').toLowerCase()
                    )
                }
                onChange={field.onChange}
                value={field.value}
                options={currencyList?.map((currency) => ({
                  value: currency,
                  label: currency,
                }))}
              />
            )}
          />
        </FormInputWrapper>
      </Grid>

      {isCoolerTenant && (
        <RHFTextInput
          name='rateData.carrierMaxRate'
          label='Max Pay'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          placeholder='0.00'
          data-testid='revenova-max-pay-input'
        />
      )}

      <Grid
        cols='1'
        gap='sm'
        width='full'
        align='stretch'
        className='col-span-2 [&>label]:h-full [&>label]:justify-end'
      >
        <FormInputWrapper
          name='specifications.serviceType'
          label='Pricing'
          hideAIHint={true}
        >
          <Controller
            name='specifications.serviceType'
            control={control}
            render={({ field }) => (
              <Select
                name={field.name}
                placeholder='Choose'
                onChange={field.onChange}
                value={field.value && field.value !== '' ? field.value : 'Spot'}
                options={pricingOptions}
              />
            )}
          />
        </FormInputWrapper>
      </Grid>
    </Grid>
  );
}

export default RatesForm;
